/**
 * Date Navigation Module
 *
 * Handles all date-related logic including navigation, limits, and display
 */

import { store } from '@wordpress/interactivity';

import { generateDateRange, dateUtils, safeCreateDate } from '../../../utils/fixtures';

/**
 * Get the center date for the date range display
 * This can be shifted by arrow navigation
 *
 * @return {string} Center date in YYYY-MM-DD format
 */
export function getCenterDate() {
  // Get the current center date from global state, default to today
  const { state } = store('big-bite-stats-perform/fixtures');
  return state.centerDate || dateUtils.toISODate();
}

/**
 * Get the "today" date for navigation limits
 *
 * @return {string} Today's date in YYYY-MM-DD format
 */
export function getTodayDate() {
  // Use actual current date
  return dateUtils.toISODate();
}

/**
 * Check if a date is within the 30-day navigation limit relative to the selected date
 *
 * @param {string} date       - Date to check in YYYY-MM-DD format
 * @param {string} centerDate - Center date to check limits against (defaults to selected date)
 * @return {boolean} True if date is within limits
 */
export function isDateWithinLimits(date, centerDate = null) {
  const { state } = store('big-bite-stats-perform/fixtures');
  const center = safeCreateDate(centerDate || state.selectedDate);
  const checkDate = safeCreateDate(date);

  if (!center || !checkDate) {
    return false;
  }

  const thirtyDaysAgo = new Date(center);
  const thirtyDaysFromNow = new Date(center);

  thirtyDaysAgo.setDate(center.getDate() - 30);
  thirtyDaysFromNow.setDate(center.getDate() + 30);

  return checkDate >= thirtyDaysAgo && checkDate <= thirtyDaysFromNow;
}

/**
 * Set the center date for the date range display
 *
 * @param {string} newCenterDate - New center date in YYYY-MM-DD format
 */
export function setCenterDate(newCenterDate) {
  const { state } = store('big-bite-stats-perform/fixtures');
  state.centerDate = newCenterDate;
}

/**
 * Update the month/year display based on the center date
 *
 * @param {string} centerDate - Center date in YYYY-MM-DD format
 */
export function updateMonthYearDisplay(centerDate) {
  const monthYearElement = document.querySelector(
    '.wp-block-big-bite-stats-perform-fixtures__nav-month-year',
  );

  if (!monthYearElement) return;

  const date = safeCreateDate(centerDate);
  if (!date) {
    monthYearElement.textContent = '—';
    return;
  }

  const monthYear = date.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  monthYearElement.textContent = monthYear;
}

/**
 * Update date navigation buttons in the DOM
 * Uses current center date for range calculation
 *
 * @param {string}   selectedDate        - Currently selected date
 * @param {Function} onDateClickCallback - Callback when date is clicked
 */
export function updateDateNavigation(selectedDate, onDateClickCallback) {
  const navDatesContainer = document.querySelector(
    '.wp-block-big-bite-stats-perform-fixtures__nav-dates',
  );

  if (!navDatesContainer) return;

  // Get current center date for the range
  const centerDate = getCenterDate();
  const dateRange = generateDateRange(centerDate);

  // Update month/year display
  updateMonthYearDisplay(centerDate);

  // Clear existing date buttons
  navDatesContainer.innerHTML = '';

  // Create new date buttons
  dateRange.forEach((dateObj) => {
    const button = document.createElement('button');
    button.className = 'wp-block-big-bite-stats-perform-fixtures__nav-date';

    if (dateObj.date === selectedDate) {
      button.classList.add('wp-block-big-bite-stats-perform-fixtures__nav-date--active');
    }

    if (dateObj.isToday) {
      button.classList.add('wp-block-big-bite-stats-perform-fixtures__nav-date--today');
    }

    // Store date data for click handler
    button.dateData = dateObj;

    // Add click handler
    button.addEventListener('click', () => onDateClickCallback(dateObj.date));

    // Create inner HTML
    const daySpan = document.createElement('span');
    daySpan.className = 'wp-block-big-bite-stats-perform-fixtures__nav-date-day';
    daySpan.textContent = dateObj.dayName;

    const numberSpan = document.createElement('span');
    numberSpan.className = 'wp-block-big-bite-stats-perform-fixtures__nav-date-number';
    numberSpan.textContent = dateObj.dayNumber;

    button.appendChild(daySpan);
    button.appendChild(numberSpan);

    navDatesContainer.appendChild(button);
  });

  // Scroll active date into view after rendering
  setTimeout(() => scrollActiveIntoView(), 100);
}

/**
 * Scroll the active date into view on mobile
 */
export function scrollActiveIntoView() {
  const navDatesContainer = document.querySelector(
    '.wp-block-big-bite-stats-perform-fixtures__nav-dates',
  );

  if (!navDatesContainer) return;

  const activeButton = document.querySelector(
    '.wp-block-big-bite-stats-perform-fixtures__nav-date--active',
  );

  if (!activeButton) return;

  // Only scroll on mobile/tablet viewport
  if (window.innerWidth <= 768) {
    activeButton.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }
}

/**
 * Update only the active date highlight without re-rendering the entire navigation
 *
 * @param {string} selectedDate - Currently selected date
 */
export function updateActiveDate(selectedDate) {
  const allDateButtons = document.querySelectorAll(
    '.wp-block-big-bite-stats-perform-fixtures__nav-date',
  );

  allDateButtons.forEach((button) => {
    button.classList.remove('wp-block-big-bite-stats-perform-fixtures__nav-date--active');

    if (button.dateData && button.dateData.date === selectedDate) {
      button.classList.add('wp-block-big-bite-stats-perform-fixtures__nav-date--active');
    }
  });

  // Scroll active date into view on mobile
  setTimeout(() => scrollActiveIntoView(), 100);
}
